import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, PartyPopper, MapPin, ArrowRight } from "lucide-react";
import { api, photoUrl } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";

const MIN_STORIES = 3;

export function ReunionsStrip() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [start, setStart] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => { api.get("/reunions?limit=10").then((r) => { setItems(r.data.items); setCount(r.data.count); }).catch(() => {}); }, []);
  useEffect(() => {
    if (items.length <= 1 || paused) return;
    const id = setInterval(() => setStart((s) => (s + 1) % items.length), 5000);
    return () => clearInterval(id);
  }, [items.length, paused]);

  if (count < MIN_STORIES) return null;
  const n = items.length;
  const visible = [0, 1, 2].map((k) => items[(start + k) % n]);
  const go = (d) => setStart((s) => (s + d + n) % n);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16" data-testid="reunions-strip"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="rounded-3xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 p-5 sm:p-8">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-emerald-700"><PartyPopper size={14} /> {t("rh_kicker")}</span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#12234A] mt-1">{t("rh_title")}</h2>
            <p className="text-slate-500 mt-1 text-sm sm:text-base">{t("rh_sub")}</p>
          </div>
          <div className="flex items-center gap-2">
            {n > 1 && (
              <>
                <button onClick={() => go(-1)} data-testid="reunions-prev" aria-label="Previous" className="h-10 w-10 rounded-full bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors flex items-center justify-center"><ChevronLeft size={18} /></button>
                <button onClick={() => go(1)} data-testid="reunions-next" aria-label="Next" className="h-10 w-10 rounded-full bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors flex items-center justify-center"><ChevronRight size={18} /></button>
              </>
            )}
            <Link to="/browse?category=lost-found&lf=reunited" data-testid="reunions-see-all" className="ml-1 text-emerald-700 font-semibold flex items-center gap-1 hover:gap-2 transition-all text-sm">{t("rh_all")} <ArrowRight size={16} /></Link>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {visible.map((r, k) => (
            <Link key={`${r.id}-${k}`} to={`/listing/${r.id}`} data-testid={`reunion-tile-${r.id}`}
              className={`${k === 1 ? "hidden sm:block" : k === 2 ? "hidden lg:block" : "block"} group bg-white rounded-2xl overflow-hidden border border-emerald-100 card-hover animate-[fadeIn_.5s_ease]`}>
              <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
                <img src={photoUrl(r.reunion_story?.photo || (r.photos || [])[0])} alt={r.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-emerald-600 text-white text-xs font-extrabold tracking-wider px-2.5 py-1 rounded-md shadow"><PartyPopper size={12} /> {t("lf_reunited")}</span>
              </div>
              <div className="p-4">
                <p className="font-display font-semibold text-[#12234A] line-clamp-1">{r.title}</p>
                <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin size={12} /> {r.city}, {r.province}</p>
                {r.reunion_story?.note && <p className="mt-2 text-sm italic text-emerald-800 line-clamp-3">“{r.reunion_story.note}”</p>}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
